import type {
  AllDayItem,
  DayTemplate,
  Goal,
  LocalDate,
  Occurrence,
  PlanPeriod,
  Task,
  WeekPattern,
} from '@planer/shared';
import { useSyncExternalStore } from 'react';
import type { MoveScope, MoveTarget } from './moves';

/** What the sheet (phone) / side panel (desktop) shows. One at a time. */
export type SheetState =
  | { type: 'add'; quick: string; time?: string; date?: LocalDate; goalId?: string }
  | { type: 'detail'; occ: Occurrence }
  | { type: 'edit'; occ: Occurrence; scope: 'this' | 'all' }
  | { type: 'move'; occ: Occurrence; target: MoveTarget; scopes: MoveScope[] }
  | { type: 'task'; task: Task | null; quick?: string }
  | { type: 'schedule-task'; task: Task; from?: LocalDate }
  | { type: 'goal'; goal: Goal | null }
  | { type: 'goal-plan'; goal: Goal; weekStart: LocalDate }
  | { type: 'template'; template: DayTemplate | null }
  | { type: 'pattern'; pattern: WeekPattern | null }
  | { type: 'period'; period: PlanPeriod | null }
  | { type: 'day'; date: LocalDate }
  | { type: 'allday'; event: AllDayItem | null; date?: LocalDate }
  | { type: 'wizard' }
  | { type: 'shortcuts' };

let state: SheetState | null = null;
const listeners = new Set<() => void>();

export function openSheet(next: SheetState | null): void {
  state = next;
  listeners.forEach((fn) => {
    fn();
  });
}
export const closeSheet = () => {
  openSheet(null);
};

const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
};

export const useSheet = () => useSyncExternalStore(subscribe, () => state);

/** Sheets that are small questions (centred dialog instead of a full sheet/panel). */
export const isCompact = (s: SheetState | null) => s?.type === 'move' || s?.type === 'shortcuts';
